import React, { useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { Tab, Tabs, Table, Spinner, Badge, Form, Button, Alert } from "react-bootstrap";
import {
  MdUpload,
  MdDownload,
  MdCloudUpload,
  MdBed,
  MdSearch,
  MdPerson
} from "react-icons/md";
import reservaManualCamasApi from "../../../services/api/reservaManualCamasApi";
import zonaApi from "../../../services/api/zonaApi";
import moduloApi from "../../../services/api/moduloApi";
import habitacionApi from "../../../services/api/habitacionApi";
import camaApi from "../../../services/api/camaApi";
import * as XLSX from "xlsx";
import "react-toastify/dist/ReactToastify.css";
import "../../../components/css/Dashboard.css";

const fechaLocalYmd = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

const extraerDiasRegimen = (codigo) => {
  if (!codigo) return 21;
  const normalizado = String(codigo).trim().toUpperCase().replace(/\s/g, "");
  const match = normalizado.match(/^(\d+)X(\d+)/);
  if (match) return parseInt(match[1], 10);
  const leading = normalizado.match(/^(\d+)/);
  if (leading) return parseInt(leading[1], 10);
  return 21;
};

const regimenCodigoTrabajador = (trabajador) =>
  trabajador?.regimen_codigo || trabajador?.regimen || null;

const calcularFechaFinEstadia = (fechaInicioYmd, regimenCodigo) => {
  const inicio = fechaInicioYmd || fechaLocalYmd();
  return sumarDiasYmd(inicio, extraerDiasRegimen(regimenCodigo));
};

const sumarDiasYmd = (ymd, dias) => {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + dias);
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${dt.getFullYear()}-${mm}-${dd}`;
};

const ReservaManualCamas = () => {
  const currentUser = JSON.parse(localStorage.getItem("user"));
  const selectedProject = JSON.parse(localStorage.getItem("selectedProject"));

  // Estados para reserva masiva
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [fileValidationErrors, setFileValidationErrors] = useState([]);
  
  // Estados para reserva individual
  const [activeTab, setActiveTab] = useState("masiva");
  const [busquedaTrabajador, setBusquedaTrabajador] = useState("");
  const [trabajadoresEncontrados, setTrabajadoresEncontrados] = useState([]);
  const [buscandoTrabajadores, setBuscandoTrabajadores] = useState(false);
  const [creandoReserva, setCreandoReserva] = useState(false);
  const [trabajadorSeleccionado, setTrabajadorSeleccionado] = useState(null);
  
  // Estados para selectores en cascada
  const [zonas, setZonas] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [habitaciones, setHabitaciones] = useState([]);
  const [camas, setCamas] = useState([]);
  
  const [zonaSeleccionada, setZonaSeleccionada] = useState("");
  const [moduloSeleccionado, setModuloSeleccionado] = useState("");
  const [habitacionSeleccionada, setHabitacionSeleccionada] = useState("");
  const [camaSeleccionada, setCamaSeleccionada] = useState("");
  
  const [cargandoZonas, setCargandoZonas] = useState(false);
  const [cargandoModulos, setCargandoModulos] = useState(false);
  const [cargandoHabitaciones, setCargandoHabitaciones] = useState(false);
  const [cargandoCamas, setCargandoCamas] = useState(false);

  const [fechaInicioReserva, setFechaInicioReserva] = useState("");
  const [fechaFinReserva, setFechaFinReserva] = useState("");

  const showToast = (type, message) => {
    if (type === "success") {
      toast.success(message, { position: "bottom-right", autoClose: 3000 });
    } else if (type === "error") {
      toast.error(message, { position: "bottom-right", autoClose: 3000 });
    } else if (type === "info") {
      toast.info(message, { position: "bottom-right", autoClose: 3000 });
    } else if (type === "warning") {
      toast.warning(message, { position: "bottom-right", autoClose: 3000 });
    }
  };

  // ========== RESERVA MASIVA ==========
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFileValidationErrors([]);
      setResults(null);
    }
  };

  const downloadTemplate = () => {
    const template = [
      {
        DNI: "12345678",
        Zona: "Zona A",
        Módulo: "Módulo 1",
        Habitación: "101",
        Cama: "1",
        "Fecha Inicio": "2026-07-01",
        "Fecha Fin": "2026-07-22",
      },
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Reserva Manual");
    XLSX.writeFile(wb, "plantilla_reserva_manual_camas.xlsx");
    showToast("info", "Plantilla descargada");
  };

  const processReservas = async () => {
    if (!file) {
      showToast("error", "Por favor seleccione un archivo");
      return;
    }

    setIsLoading(true);
    setResults(null);
    setFileValidationErrors([]);

    try {
      // Paso 1: Validar el archivo primero
      showToast("info", "Validando archivo...");
      const validationResponse = await reservaManualCamasApi.validateFile(
        file,
        selectedProject?.id
      );

      const vData = validationResponse.data || {};
      const erroresList = Array.isArray(vData.errores) ? vData.errores : [];
      const totalErrores =
        typeof vData.total_errores === "number" ? vData.total_errores : erroresList.length;

      // El backend puede responder 200 OK con success: false y errores detallados (sin campo message)
      if (totalErrores > 0 || erroresList.length > 0) {
        setFileValidationErrors(erroresList);
        const n = Math.max(totalErrores, erroresList.length);
        showToast(
          "error",
          `❌ El archivo contiene ${n} error(es). Corrija el detalle indicado abajo antes de continuar.`
        );
        setIsLoading(false);
        return;
      }

      if (!vData.success) {
        showToast("error", vData.message || "Error al validar el archivo");
        setIsLoading(false);
        return;
      }

      const total_filas = vData.total_filas;

      // Si la validación fue exitosa, continuar con el procesamiento
      showToast("success", `✅ Validación exitosa. Procesando ${total_filas} reservas...`);

      // Paso 2: Procesar las reservas (el backend validará nuevamente y solo procesará si todo está OK)
      const response = await reservaManualCamasApi.procesarReservas(
        file,
        selectedProject?.id
      );

      // Manejar respuesta del procesamiento
      if (response.data?.success) {
        setResults(response.data);
        const { asignaciones_nuevas, asignaciones_existentes, asignaciones_fallidas } = response.data;
        
        if (asignaciones_fallidas === 0 && asignaciones_existentes === 0) {
          showToast("success", `✅ Todas las reservas se procesaron exitosamente (${asignaciones_nuevas} nuevas)`);
        } else if (asignaciones_fallidas === 0) {
          showToast(
            "success",
            `✅ Procesamiento completado: ${asignaciones_nuevas} nuevas, ${asignaciones_existentes} ya existentes`
          );
        } else {
          // Esto NO debería ocurrir con la nueva validación exhaustiva
          showToast(
            "warning",
            `⚠️ Procesamiento completado con advertencias: ${asignaciones_nuevas} nuevas, ${asignaciones_existentes} ya existentes, ${asignaciones_fallidas} fallidas`
          );
        }
      } else {
        // El backend rechazó el procesamiento por errores de validación
        const { asignaciones_fallidas, detalle_fallidas } = response.data || {};
        if (asignaciones_fallidas > 0 && detalle_fallidas) {
          setFileValidationErrors(detalle_fallidas);
        }
        showToast("error", response.data?.message || "Error al procesar las reservas");
      }
    } catch (error) {
      console.error("Error al procesar reservas:", error);
      showToast(
        "error",
        error.response?.data?.detail || error.response?.data?.message || "Error al procesar las reservas"
      );
    } finally {
      setIsLoading(false);
    }
  };

  // ========== RESERVA INDIVIDUAL ==========
  const handleBuscarTrabajadores = async () => {
    if (!busquedaTrabajador || busquedaTrabajador.trim().length < 2) {
      showToast("error", "Ingrese al menos 2 caracteres para buscar");
      return;
    }

    setBuscandoTrabajadores(true);
    setTrabajadoresEncontrados([]);
    setTrabajadorSeleccionado(null);

    try {
      const response = await reservaManualCamasApi.buscarTrabajadores(
        busquedaTrabajador.trim(),
        selectedProject?.id
      );

      if (response.data?.success) {
        setTrabajadoresEncontrados(response.data.data || []);
        if (response.data.data?.length === 0) {
          showToast("info", "No se encontraron trabajadores");
        }
      } else {
        showToast("error", "Error al buscar trabajadores");
      }
    } catch (error) {
      console.error("Error al buscar trabajadores:", error);
      showToast("error", error.response?.data?.detail || "Error al buscar trabajadores");
    } finally {
      setBuscandoTrabajadores(false);
    }
  };

  const handleSeleccionarTrabajador = (trabajador) => {
    setTrabajadorSeleccionado(trabajador);
    const inicio = fechaLocalYmd();
    setFechaInicioReserva(inicio);
    setFechaFinReserva(
      calcularFechaFinEstadia(inicio, regimenCodigoTrabajador(trabajador))
    );
    // Limpiar selectores
    setZonaSeleccionada("");
    setModuloSeleccionado("");
    setHabitacionSeleccionada("");
    setCamaSeleccionada("");
    setModulos([]);
    setHabitaciones([]);
    setCamas([]);
    // Cargar zonas
    cargarZonas();
  };

  // Funciones para cargar datos en cascada
  const cargarZonas = async () => {
    setCargandoZonas(true);
    try {
      const response = await zonaApi.getZonasByProyecto(selectedProject?.id);
      console.log("Response zonas:", response);
      
      // El endpoint retorna directamente la lista, no un objeto con success/data
      if (Array.isArray(response.data)) {
        setZonas(response.data);
      } else if (response.data?.success) {
        setZonas(response.data.data || []);
      } else {
        setZonas([]);
      }
    } catch (error) {
      console.error("Error al cargar zonas:", error);
      showToast("error", "Error al cargar zonas");
      setZonas([]);
    } finally {
      setCargandoZonas(false);
    }
  };

  const cargarModulos = async (zonaId) => {
    if (!zonaId) return;
    setCargandoModulos(true);
    setModulos([]);
    setHabitaciones([]);
    setCamas([]);
    setModuloSeleccionado("");
    setHabitacionSeleccionada("");
    setCamaSeleccionada("");
    
    try {
      const response = await moduloApi.getModulosByZona(zonaId);
      console.log("Response modulos:", response);
      
      // El endpoint retorna directamente la lista
      if (Array.isArray(response.data)) {
        setModulos(response.data);
      } else if (response.data?.success) {
        setModulos(response.data.data || []);
      } else {
        setModulos([]);
      }
    } catch (error) {
      console.error("Error al cargar módulos:", error);
      showToast("error", "Error al cargar módulos");
      setModulos([]);
    } finally {
      setCargandoModulos(false);
    }
  };

  const cargarHabitaciones = async (moduloId) => {
    if (!moduloId) return;
    setCargandoHabitaciones(true);
    setHabitaciones([]);
    setCamas([]);
    setHabitacionSeleccionada("");
    setCamaSeleccionada("");
    
    try {
      const response = await habitacionApi.getHabitacionesByModulo(moduloId);
      console.log("Response habitaciones:", response);
      
      // El endpoint retorna directamente la lista
      if (Array.isArray(response.data)) {
        setHabitaciones(response.data);
      } else if (response.data?.success) {
        setHabitaciones(response.data.data || []);
      } else {
        setHabitaciones([]);
      }
    } catch (error) {
      console.error("Error al cargar habitaciones:", error);
      showToast("error", "Error al cargar habitaciones");
      setHabitaciones([]);
    } finally {
      setCargandoHabitaciones(false);
    }
  };

  const cargarCamas = async (habitacionId) => {
    if (!habitacionId) return;
    setCargandoCamas(true);
    setCamas([]);
    setCamaSeleccionada("");
    
    try {
      // Cargar solo camas libres
      const response = await camaApi.getCamasLibresByHabitacion(habitacionId);
      console.log("Response camas:", response);
      
      // El endpoint retorna directamente la lista
      let camasData = [];
      if (Array.isArray(response.data)) {
        camasData = response.data;
      } else if (response.data?.success) {
        camasData = response.data.data || [];
      }
      
      setCamas(camasData);
      if (camasData.length === 0) {
        showToast("warning", "No hay camas disponibles en esta habitación");
      }
    } catch (error) {
      console.error("Error al cargar camas:", error);
      showToast("error", "Error al cargar camas");
      setCamas([]);
    } finally {
      setCargandoCamas(false);
    }
  };

  const handleZonaChange = (e) => {
    const zonaId = e.target.value;
    setZonaSeleccionada(zonaId);
    if (zonaId) {
      cargarModulos(zonaId);
    } else {
      setModulos([]);
      setHabitaciones([]);
      setCamas([]);
      setModuloSeleccionado("");
      setHabitacionSeleccionada("");
      setCamaSeleccionada("");
    }
  };

  const handleModuloChange = (e) => {
    const moduloId = e.target.value;
    setModuloSeleccionado(moduloId);
    if (moduloId) {
      cargarHabitaciones(moduloId);
    } else {
      setHabitaciones([]);
      setCamas([]);
      setHabitacionSeleccionada("");
      setCamaSeleccionada("");
    }
  };

  const handleHabitacionChange = (e) => {
    const habitacionId = e.target.value;
    setHabitacionSeleccionada(habitacionId);
    if (habitacionId) {
      cargarCamas(habitacionId);
    } else {
      setCamas([]);
      setCamaSeleccionada("");
    }
  };

  const handleCamaChange = (e) => {
    setCamaSeleccionada(e.target.value);
  };

  const handleFechaInicioReservaChange = (e) => {
    const inicio = e.target.value;
    setFechaInicioReserva(inicio);
    if (trabajadorSeleccionado && inicio) {
      setFechaFinReserva(
        calcularFechaFinEstadia(inicio, regimenCodigoTrabajador(trabajadorSeleccionado))
      );
    }
  };

  const handleCrearReservaIndividual = async () => {
    if (!trabajadorSeleccionado) {
      showToast("error", "Seleccione un trabajador");
      return;
    }

    if (!zonaSeleccionada || !moduloSeleccionado || !habitacionSeleccionada || !camaSeleccionada) {
      showToast("error", "Complete todos los campos de ubicación");
      return;
    }

    if (!fechaInicioReserva) {
      showToast("error", "La fecha inicio de estadía es obligatoria");
      return;
    }

    if (!fechaFinReserva) {
      showToast("error", "La fecha fin de estadía es obligatoria");
      return;
    }

    if (fechaFinReserva < fechaInicioReserva) {
      showToast("error", "La fecha fin no puede ser anterior a la fecha inicio");
      return;
    }

    const inicioEnvio = fechaInicioReserva;
    const finEnvio = fechaFinReserva;

    setCreandoReserva(true);

    try {
      // Obtener los nombres para enviar al backend (el backend actual espera nombres)
      const zonaNombre = zonas.find(z => z.id === parseInt(zonaSeleccionada))?.nombre;
      const moduloNombre = modulos.find(m => m.id === parseInt(moduloSeleccionado))?.nombre;
      const habitacionNumero = habitaciones.find(h => h.id === parseInt(habitacionSeleccionada))?.numero;
      const camaCodigo = camas.find(c => c.id === parseInt(camaSeleccionada))?.codigo;

      const response = await reservaManualCamasApi.crearReservaIndividual(
        trabajadorSeleccionado.id,
        zonaNombre,
        moduloNombre,
        habitacionNumero,
        camaCodigo,
        selectedProject?.id,
        {
          fechaInicioEstadia: inicioEnvio,
          fechaFinEstadia: finEnvio,
        }
      );

      if (response.data?.success) {
        const ini = response.data.fecha_inicio_estadia || fechaInicioReserva;
        const fin = response.data.fecha_fin_estadia || fechaFinReserva;
        showToast(
          "success",
          ini && fin
            ? `Reserva creada (${ini} → ${fin})`
            : "Reserva creada exitosamente"
        );
        // Limpiar formulario
        setTrabajadorSeleccionado(null);
        setFechaInicioReserva("");
        setFechaFinReserva("");
        setZonaSeleccionada("");
        setModuloSeleccionado("");
        setHabitacionSeleccionada("");
        setCamaSeleccionada("");
        setZonas([]);
        setModulos([]);
        setHabitaciones([]);
        setCamas([]);
        setBusquedaTrabajador("");
        setTrabajadoresEncontrados([]);
      } else {
        if (response.data?.tiene_reserva) {
          showToast("warning", response.data.message);
        } else {
          showToast("error", response.data?.message || "Error al crear la reserva");
        }
      }
    } catch (error) {
      console.error("Error al crear reserva:", error);
      showToast("error", error.response?.data?.detail || "Error al crear la reserva");
    } finally {
      setCreandoReserva(false);
    }
  };

  return (
    <>
      <style>
        {`
          .dh-carga-bg {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            min-height: 100vh;
            padding: 2rem 0;
          }
          
          .dh-card-main {
            border-radius: 16px;
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
            border: none;
            overflow: hidden;
          }
          
          .dh-card-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border: none;
            padding: 1.5rem;
          }
          
          .dh-header-title {
            font-weight: 600;
            color: white;
          }
          
          .dh-form-control {
            border-radius: 8px;
            border: 1px solid #e0e0e0;
            padding: 0.75rem;
            transition: all 0.3s;
          }
          
          .dh-form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
          }
          
          .dh-btn-primary {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border: none;
            border-radius: 8px;
            padding: 0.75rem 1.5rem;
            font-weight: 500;
            transition: all 0.3s;
          }
          
          .dh-btn-primary:hover {
            transform: translateY(-2px);
            box-shadow: 0 5px 15px rgba(102, 126, 234, 0.4);
          }
          
          .dh-btn-secondary {
            background: #6c757d;
            border: none;
            border-radius: 8px;
            padding: 0.75rem 1.5rem;
            font-weight: 500;
          }
          
          .dh-tabs .nav-link {
            color: #667eea;
            border: none;
            border-bottom: 2px solid transparent;
            padding: 1rem 1.5rem;
          }
          
          .dh-tabs .nav-link.active {
            color: #667eea;
            border-bottom: 2px solid #667eea;
            background: transparent;
          }
        `}
      </style>

      <div className="dh-carga-bg">
        <ToastContainer />
        <div className="container-fluid py-4">
          <div className="row justify-content-center">
            <div className="col-12 col-lg-10 col-xl-8">
              <div className="card dh-card-main">
                <div className="card-header dh-card-header">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <MdBed className="me-3" size={28} />
                      <h3 className="dh-header-title mb-0">
                        Reserva Manual de Camas
                      </h3>
                    </div>
                  </div>
                </div>
                <div className="card-body p-4">
                  {/* Proyecto */}
                  <div className="mb-4">
                    <label className="form-label fw-bold">Proyecto</label>
                    <input
                      type="text"
                      className="form-control dh-form-control"
                      value={selectedProject?.nombre || "No seleccionado"}
                      disabled
                    />
                  </div>

                  {/* Pestañas */}
                  <Tabs
                    id="reserva-manual-tabs"
                    activeKey={activeTab}
                    onSelect={(k) => setActiveTab(k)}
                    className="mb-4 dh-tabs"
                  >
                    <Tab eventKey="masiva" title="Reserva Masiva">
                      <div className="mt-4">
                        <h5 className="mb-3">Carga Masiva desde Excel</h5>
                        <p className="text-muted mb-2">
                          Columnas obligatorias: <strong>DNI</strong>, <strong>Zona</strong>,{" "}
                          <strong>Módulo</strong>, <strong>Habitación</strong>, <strong>Cama</strong>,{" "}
                          <strong>Fecha Inicio</strong> y <strong>Fecha Fin</strong>.
                        </p>
                        <p className="text-muted mb-4">
                          Use formato <strong>YYYY-MM-DD</strong> o <strong>DD/MM/YYYY</strong> en las fechas.
                          La fecha fin debe ser igual o posterior a la fecha inicio.
                        </p>

                        <div className="mb-3">
                          <Button
                            variant="outline-primary"
                            onClick={downloadTemplate}
                            className="me-2"
                          >
                            <MdDownload className="me-2" />
                            Descargar Plantilla
                          </Button>
                        </div>

                        <div className="mb-3">
                          <label className="form-label fw-bold">Seleccionar Archivo Excel</label>
                          <input
                            type="file"
                            className="form-control dh-form-control"
                            accept=".xlsx,.xls"
                            onChange={handleFileChange}
                          />
                          {file && (
                            <small className="text-muted">
                              Archivo seleccionado: {file.name}
                            </small>
                          )}
                        </div>

                        <div className="mb-3">
                          <Button
                            variant="success"
                            onClick={processReservas}
                            disabled={!file || isLoading}
                            className="dh-btn-primary"
                          >
                            {isLoading ? (
                              <>
                                <Spinner size="sm" className="me-2" />
                                Procesando...
                              </>
                            ) : (
                              <>
                                <MdCloudUpload className="me-2" />
                                Validar y Procesar Reservas
                              </>
                            )}
                          </Button>
                        </div>

                        {/* Errores de validación */}
                        {fileValidationErrors.length > 0 && (
                          <Alert variant="danger" className="mt-3">
                            <strong>⛔ ERRORES CRÍTICOS ENCONTRADOS ({fileValidationErrors.length}):</strong>
                            <p className="mb-2 mt-2">
                              <strong>El archivo NO se ha procesado.</strong> Debe corregir TODOS los errores antes de continuar.
                            </p>
                            <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                              <Table striped bordered hover size="sm" className="mt-2">
                                <thead>
                                  <tr>
                                    <th style={{ width: "80px" }}>Fila</th>
                                    <th style={{ width: "120px" }}>DNI</th>
                                    <th>Error(es)</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {fileValidationErrors.map((error, idx) => (
                                    <tr key={idx}>
                                      <td>{error.fila}</td>
                                      <td>{error.dni || "-"}</td>
                                      <td>
                                        {Array.isArray(error.errores) ? (
                                          <ul className="mb-0">
                                            {error.errores.map((err, errIdx) => (
                                              <li key={errIdx} className={err.includes("CONFLICTO") ? "text-danger fw-bold" : ""}>
                                                {err}
                                              </li>
                                            ))}
                                          </ul>
                                        ) : (
                                          <span className={error.error?.includes("CONFLICTO") ? "text-danger fw-bold" : ""}>
                                            {error.error || "Error desconocido"}
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          </Alert>
                        )}

                        {/* Resultados */}
                        {results && (
                          <div className="mt-4">
                            <Alert variant={results.asignaciones_fallidas === 0 ? "success" : "warning"}>
                              <strong>Resultado del Procesamiento:</strong>
                              <ul className="mb-0 mt-2">
                                <li><strong>Reservas Nuevas:</strong> {results.asignaciones_nuevas}</li>
                                <li><strong>Reservas Ya Existentes:</strong> {results.asignaciones_existentes}</li>
                                <li><strong>Reservas Fallidas:</strong> {results.asignaciones_fallidas}</li>
                              </ul>
                            </Alert>

                            {/* Detalle de Reservas Nuevas */}
                            {results.detalle_nuevas?.length > 0 && (
                              <div className="mt-3">
                                <h6 className="text-success">✅ Detalle de Reservas Nuevas ({results.detalle_nuevas.length}):</h6>
                                <Table striped bordered hover size="sm">
                                  <thead>
                                    <tr>
                                      <th>Fila</th>
                                      <th>DNI</th>
                                      <th>Trabajador</th>
                                      <th>Zona</th>
                                      <th>Módulo</th>
                                      <th>Habitación</th>
                                      <th>Cama</th>
                                      <th>Inicio estadia</th>
                                      <th>Fin estadia</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {results.detalle_nuevas.map((item, idx) => (
                                      <tr key={idx}>
                                        <td>{item.fila}</td>
                                        <td>{item.dni}</td>
                                        <td>{item.trabajador}</td>
                                        <td>{item.zona}</td>
                                        <td>{item.modulo}</td>
                                        <td>{item.habitacion}</td>
                                        <td>{item.numero_cama}</td>
                                        <td>{item.fecha_inicio_estadia || "—"}</td>
                                        <td>{item.fecha_fin_estadia || "—"}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </div>
                            )}

                            {/* Detalle de Reservas Ya Existentes */}
                            {results.detalle_existentes?.length > 0 && (
                              <div className="mt-3">
                                <h6 className="text-info">ℹ️ Detalle de Reservas Ya Existentes ({results.detalle_existentes.length}):</h6>
                                <Table striped bordered hover size="sm">
                                  <thead>
                                    <tr>
                                      <th>Fila</th>
                                      <th>DNI</th>
                                      <th>Trabajador</th>
                                      <th>Zona</th>
                                      <th>Módulo</th>
                                      <th>Habitación</th>
                                      <th>Cama</th>
                                      <th>Estado</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {results.detalle_existentes.map((item, idx) => (
                                      <tr key={idx}>
                                        <td>{item.fila}</td>
                                        <td>{item.dni}</td>
                                        <td>{item.trabajador}</td>
                                        <td>{item.zona}</td>
                                        <td>{item.modulo}</td>
                                        <td>{item.habitacion}</td>
                                        <td>{item.numero_cama}</td>
                                        <td>
                                          <Badge bg="info">{item.estado_asignacion}</Badge>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </div>
                            )}

                            {/* Detalle de Reservas Fallidas */}
                            {results.detalle_fallidas?.length > 0 && (
                              <div className="mt-3">
                                <h6 className="text-danger">❌ Detalle de Reservas Fallidas ({results.detalle_fallidas.length}):</h6>
                                <Table striped bordered hover size="sm">
                                  <thead>
                                    <tr>
                                      <th>Fila</th>
                                      <th>DNI</th>
                                      <th>Error</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {results.detalle_fallidas.map((item, idx) => (
                                      <tr key={idx}>
                                        <td>{item.fila}</td>
                                        <td>{item.dni}</td>
                                        <td className="text-danger">{item.error}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </Tab>

                    <Tab eventKey="individual" title="Reserva Individual">
                      <div className="mt-4">
                        <h5 className="mb-3">Reserva Individual</h5>

                        {/* Búsqueda de trabajador */}
                        <div className="mb-4">
                          <label className="form-label fw-bold">Buscar Trabajador</label>
                          <div className="d-flex gap-2">
                            <input
                              type="text"
                              className="form-control dh-form-control"
                              placeholder="Ingrese DNI o nombre/apellidos"
                              value={busquedaTrabajador}
                              onChange={(e) => setBusquedaTrabajador(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === "Enter") {
                                  handleBuscarTrabajadores();
                                }
                              }}
                            />
                            <Button
                              variant="primary"
                              onClick={handleBuscarTrabajadores}
                              disabled={buscandoTrabajadores}
                              className="dh-btn-primary"
                            >
                              {buscandoTrabajadores ? (
                                <Spinner size="sm" />
                              ) : (
                                <MdSearch />
                              )}
                            </Button>
                          </div>
                        </div>

                        {/* Lista de trabajadores encontrados */}
                        {trabajadoresEncontrados.length > 0 && (
                          <div className="mb-4">
                            <h6>Resultados de Búsqueda:</h6>
                            <Table striped bordered hover size="sm">
                              <thead>
                                <tr>
                                  <th>Documento</th>
                                  <th>Nombre Completo</th>
                                  <th>Cargo</th>
                                  <th>Área</th>
                                  <th>Género</th>
                                  <th>Régimen</th>
                                  <th>Acción</th>
                                </tr>
                              </thead>
                              <tbody>
                                {trabajadoresEncontrados.map((trabajador) => (
                                  <tr
                                    key={trabajador.id}
                                    style={{
                                      backgroundColor: trabajador.id === trabajadorSeleccionado?.id ? "#e3f2fd" : "inherit"
                                    }}
                                  >
                                    <td>{trabajador.numero_documento_identidad}</td>
                                    <td>{trabajador.nombre_completo}</td>
                                    <td>{trabajador.cargo || "-"}</td>
                                    <td>{trabajador.area || "-"}</td>
                                    <td>{trabajador.genero || "-"}</td>
                                    <td>{trabajador.regimen_codigo || "-"}</td>
                                    <td>
                                      {trabajador.tiene_reserva ? (
                                        <Badge bg="warning">
                                          Ya tiene reserva
                                        </Badge>
                                      ) : (
                                        <Button
                                          size="sm"
                                          variant={trabajador.id === trabajadorSeleccionado?.id ? "success" : "primary"}
                                          onClick={() => handleSeleccionarTrabajador(trabajador)}
                                          className="dh-btn-primary"
                                        >
                                          {trabajador.id === trabajadorSeleccionado?.id ? "Seleccionado" : "Seleccionar"}
                                        </Button>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        )}

                        {/* Formulario de ubicación */}
                        {trabajadorSeleccionado && !trabajadorSeleccionado.tiene_reserva && (
                          <div className="mt-4 p-3 border rounded">
                            <h6 className="mb-3">Datos de Ubicación de la Cama</h6>
                            <p className="text-muted small mb-3">
                              Trabajador seleccionado: <strong>{trabajadorSeleccionado.nombre_completo}</strong> (DNI: {trabajadorSeleccionado.numero_documento_identidad})
                            </p>

                            <div className="row">
                              <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">Zona</label>
                                <select
                                  className="form-select dh-form-control"
                                  value={zonaSeleccionada}
                                  onChange={handleZonaChange}
                                  disabled={cargandoZonas}
                                >
                                  <option value="">Seleccione una zona</option>
                                  {zonas.map((zona) => (
                                    <option key={zona.id} value={zona.id}>
                                      {zona.nombre}
                                    </option>
                                  ))}
                                </select>
                                {cargandoZonas && (
                                  <small className="text-muted">Cargando zonas...</small>
                                )}
                              </div>
                              <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">Módulo</label>
                                <select
                                  className="form-select dh-form-control"
                                  value={moduloSeleccionado}
                                  onChange={handleModuloChange}
                                  disabled={!zonaSeleccionada || cargandoModulos}
                                >
                                  <option value="">
                                    {zonaSeleccionada ? "Seleccione un módulo" : "Primero seleccione una zona"}
                                  </option>
                                  {modulos.map((modulo) => (
                                    <option key={modulo.id} value={modulo.id}>
                                      {modulo.nombre}
                                    </option>
                                  ))}
                                </select>
                                {cargandoModulos && (
                                  <small className="text-muted">Cargando módulos...</small>
                                )}
                              </div>
                              <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">Habitación</label>
                                <select
                                  className="form-select dh-form-control"
                                  value={habitacionSeleccionada}
                                  onChange={handleHabitacionChange}
                                  disabled={!moduloSeleccionado || cargandoHabitaciones}
                                >
                                  <option value="">
                                    {moduloSeleccionado ? "Seleccione una habitación" : "Primero seleccione un módulo"}
                                  </option>
                                  {habitaciones.map((habitacion) => (
                                    <option key={habitacion.id} value={habitacion.id}>
                                      {habitacion.numero}
                                    </option>
                                  ))}
                                </select>
                                {cargandoHabitaciones && (
                                  <small className="text-muted">Cargando habitaciones...</small>
                                )}
                              </div>
                              <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">Cama</label>
                                <select
                                  className="form-select dh-form-control"
                                  value={camaSeleccionada}
                                  onChange={handleCamaChange}
                                  disabled={!habitacionSeleccionada || cargandoCamas}
                                >
                                  <option value="">
                                    {habitacionSeleccionada ? "Seleccione una cama" : "Primero seleccione una habitación"}
                                  </option>
                                  {camas.map((cama) => (
                                    <option key={cama.id} value={cama.id}>
                                      {cama.codigo} {cama.estado_cama_nombre ? `(${cama.estado_cama_nombre})` : ""}
                                    </option>
                                  ))}
                                </select>
                                {cargandoCamas && (
                                  <small className="text-muted">Cargando camas...</small>
                                )}
                                {camas.length === 0 && habitacionSeleccionada && !cargandoCamas && (
                                  <small className="text-danger">No hay camas disponibles</small>
                                )}
                              </div>
                            </div>

                            <div className="row mt-2">
                              <div className="col-12">
                                <h6 className="mb-2">Fechas de la reserva <span className="text-danger">*</span></h6>
                                <Alert variant="info" className="py-2 small mb-3">
                                  Ambas fechas son <strong>obligatorias</strong>. Al elegir la fecha inicio
                                  se sugiere la fecha fin según el régimen del trabajador; puede ajustarla
                                  manualmente.
                                </Alert>
                              </div>
                              <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">
                                  Fecha inicio estadía <span className="text-danger">*</span>
                                </label>
                                <input
                                  type="date"
                                  className="form-control dh-form-control"
                                  value={fechaInicioReserva}
                                  onChange={handleFechaInicioReservaChange}
                                  required
                                />
                              </div>
                              <div className="col-md-6 mb-3">
                                <label className="form-label fw-bold">
                                  Fecha fin estadía <span className="text-danger">*</span>
                                </label>
                                <input
                                  type="date"
                                  className="form-control dh-form-control"
                                  value={fechaFinReserva}
                                  min={fechaInicioReserva || undefined}
                                  onChange={(e) => setFechaFinReserva(e.target.value)}
                                  required
                                />
                              </div>
                            </div>

                            <Button
                              variant="success"
                              onClick={handleCrearReservaIndividual}
                              disabled={
                                creandoReserva ||
                                !zonaSeleccionada ||
                                !moduloSeleccionado ||
                                !habitacionSeleccionada ||
                                !camaSeleccionada ||
                                !fechaInicioReserva ||
                                !fechaFinReserva
                              }
                              className="dh-btn-primary"
                            >
                              {creandoReserva ? (
                                <>
                                  <Spinner size="sm" className="me-2" />
                                  Creando Reserva...
                                </>
                              ) : (
                                <>
                                  <MdBed className="me-2" />
                                  Crear Reserva
                                </>
                              )}
                            </Button>
                          </div>
                        )}

                        {trabajadorSeleccionado?.tiene_reserva && (
                          <Alert variant="info" className="mt-3">
                            <strong>El trabajador ya tiene una reserva:</strong>
                            <ul className="mb-0 mt-2">
                              <li>Estado: {trabajadorSeleccionado.reserva_info?.estado_asignacion}</li>
                              <li>Zona: {trabajadorSeleccionado.reserva_info?.cama?.zona}</li>
                              <li>Módulo: {trabajadorSeleccionado.reserva_info?.cama?.modulo}</li>
                              <li>Habitación: {trabajadorSeleccionado.reserva_info?.cama?.habitacion}</li>
                              <li>Cama: {trabajadorSeleccionado.reserva_info?.cama?.numero_cama}</li>
                            </ul>
                          </Alert>
                        )}
                      </div>
                    </Tab>
                  </Tabs>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReservaManualCamas;
